import { useState, useEffect, useRef } from 'react';

export const useOKXPerpFeed = () => {
  const [perpData, setPerpData] = useState({
    markPrice: 0,
    indexPrice: 0,
    fundingRate: 0,
    nextFundingTime: 0,
    volume24h: 0,
    openInterest: 0,
    bid: 0,
    ask: 0,
    spread: 0,
    lastUpdate: null,
    isLive: false
  });

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connectWebSocket = () => {
    try {
      console.log('🔗 Connecting to OKX Perpetual Futures WebSocket...');
      
      // OKX Public WebSocket for Perpetual Futures
      wsRef.current = new WebSocket('wss://ws.okx.com:8443/ws/v5/public');
      
      wsRef.current.onopen = () => {
        console.log('✅ OKX Perp WebSocket connected');
        reconnectAttempts.current = 0;
        
        // Subscribe to multiple perpetual futures channels
        const subscriptions = [
          // Mark price and funding rate
          {
            "op": "subscribe",
            "args": [{
              "channel": "mark-price",
              "instId": "BTC-USD-SWAP"
            }]
          },
          // Funding rate
          {
            "op": "subscribe", 
            "args": [{
              "channel": "funding-rate",
              "instId": "BTC-USD-SWAP"
            }]
          },
          // 24h ticker with volume and open interest
          {
            "op": "subscribe",
            "args": [{
              "channel": "tickers",
              "instId": "BTC-USD-SWAP"
            }]
          },
          // Order book for bid/ask
          {
            "op": "subscribe",
            "args": [{
              "channel": "books5",
              "instId": "BTC-USD-SWAP"
            }]
          }
        ];
        
        // Send all subscriptions
        subscriptions.forEach(sub => {
          wsRef.current.send(JSON.stringify(sub));
        });
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.data && data.data.length > 0) {
            const channelData = data.data[0];
            
            // Process different channel types
            switch (data.arg?.channel) {
              case 'mark-price':
                setPerpData(prev => ({
                  ...prev,
                  markPrice: parseFloat(channelData.markPx),
                  indexPrice: parseFloat(channelData.idxPx),
                  lastUpdate: new Date().toISOString(),
                  isLive: true
                }));
                console.log('📊 OKX Perp Mark Price:', channelData.markPx);
                break;
                
              case 'funding-rate':
                const nextFundingMs = parseInt(channelData.nextFundingTime);
                setPerpData(prev => ({
                  ...prev,
                  fundingRate: parseFloat(channelData.fundingRate),
                  nextFundingTime: nextFundingMs,
                  lastUpdate: new Date().toISOString(),
                  isLive: true
                }));
                console.log('💰 OKX Funding Rate:', channelData.fundingRate);
                break;
                
              case 'tickers':
                setPerpData(prev => ({
                  ...prev,
                  volume24h: parseFloat(channelData.vol24h),
                  openInterest: parseFloat(channelData.oi),
                  lastUpdate: new Date().toISOString(),
                  isLive: true
                }));
                console.log('📈 OKX Volume/OI:', channelData.vol24h, channelData.oi);
                break;
                
              case 'books5':
                const bids = channelData.bids;
                const asks = channelData.asks;
                if (bids.length > 0 && asks.length > 0) {
                  const bid = parseFloat(bids[0][0]);
                  const ask = parseFloat(asks[0][0]);
                  const spread = ask - bid;
                  
                  setPerpData(prev => ({
                    ...prev,
                    bid: bid,
                    ask: ask,
                    spread: spread,
                    lastUpdate: new Date().toISOString(),
                    isLive: true
                  }));
                  console.log('💱 OKX Bid/Ask:', bid, ask, 'Spread:', spread.toFixed(2));
                }
                break;
            }
          }
        } catch (error) {
          console.warn('⚠️ OKX WebSocket message parsing error:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('🔌 OKX Perp WebSocket disconnected:', event.code, event.reason);
        setPerpData(prev => ({ ...prev, isLive: false }));
        
        // Attempt reconnection with backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`🔄 Reconnecting in ${delay}ms... (attempt ${reconnectAttempts.current + 1})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('❌ OKX Perp WebSocket error:', error);
        setPerpData(prev => ({ ...prev, isLive: false }));
      };

    } catch (error) {
      console.error('❌ Failed to connect to OKX Perp WebSocket:', error);
    }
  };

  // Connect on mount
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Calculate funding fee impact
  const calculateFundingImpact = (positionSize, leverage = 10) => {
    const notionalValue = positionSize * perpData.markPrice * leverage;
    const fundingFee = notionalValue * perpData.fundingRate;
    return fundingFee;
  };

  // Calculate next funding time
  const getTimeToNextFunding = () => {
    if (!perpData.nextFundingTime) return 'Unknown';
    
    const now = Date.now();
    const timeLeft = perpData.nextFundingTime - now;
    
    if (timeLeft <= 0) return 'Due now';
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  return {
    ...perpData,
    calculateFundingImpact,
    getTimeToNextFunding,
    // Legacy compatibility
    btcPrice: perpData.markPrice,
    bidAskSpread: perpData.spread
  };
};
